import { useEffect, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'

import { formatList } from '../../lib/format'
import { getSharedContacts, type SharedContactKind, type SharedContactMatch } from './api'

/** The details the server compares; anything else it names reads as a translated generic. */
const KNOWN_FIELDS = new Set(['Address', 'Phone', 'Email'])

const PATHS: Readonly<Record<SharedContactKind, string>> = {
  Broker: '/brokers',
  Carrier: '/carriers',
}

type Reading = { status: 'failed' } | { status: 'read'; matches: SharedContactMatch[] }

type Props = {
  kind: SharedContactKind
  id: string
  /** The record's saved version: a new one is judged afresh, so a save that changed the details shows at once. */
  version: string | null
}

/**
 * Other brokers and carriers reached the way this one is (TRKC-99): two companies at one
 * address, phone or email can be one company under two names. A warning, never a block.
 *
 * Says nothing until it has read and nothing when nobody shares anything: the warning is
 * only worth the space when there is something to check. Read again on every new saved
 * version — and meanwhile the last reading of the same record stays on screen, because
 * the section sits above the form, and dropping it while a save is judged again would
 * pull the whole form up and push it back down under the hand that just pressed Save.
 * A reading of another record is never shown, not even for a moment.
 */
export function SharedContactsNotice({ kind, id, version }: Props) {
  const { t, i18n } = useTranslation()
  const headingId = useId()
  const subject = `${kind}:${id}`
  const [last, setLast] = useState<{ subject: string; reading: Reading } | null>(null)

  useEffect(() => {
    let active = true

    getSharedContacts(kind, id)
      .then((view) => {
        if (active) {
          setLast({ subject: `${kind}:${id}`, reading: { status: 'read', matches: view.matches } })
        }
      })
      .catch(() => {
        if (active) {
          setLast({ subject: `${kind}:${id}`, reading: { status: 'failed' } })
        }
      })

    return () => {
      active = false
    }
  }, [kind, id, version])

  const reading = last?.subject === subject ? last.reading : null

  if (reading === null) {
    return null
  }

  // A check nobody could make is not the same as a check that found nothing.
  if (reading.status === 'failed') {
    return <p className="muted shared-contacts__unread">{t('sharedContacts.unread')}</p>
  }

  if (reading.matches.length === 0) {
    return null
  }

  const fieldName = (field: string) =>
    KNOWN_FIELDS.has(field) ? t(`sharedContacts.fields.${field}`) : t('sharedContacts.fields.other')

  return (
    <section className="alert alert--warning shared-contacts" aria-labelledby={headingId}>
      <h2 id={headingId} className="shared-contacts__title">
        {t('sharedContacts.title')}
      </h2>
      <p>{t('sharedContacts.lead')}</p>
      <ul className="shared-contacts__list">
        {reading.matches.map((match) => (
          <li key={`${match.kind}:${match.id}`}>
            <Link to={`${PATHS[match.kind]}/${match.id}`}>{match.name}</Link>
            {' · '}
            {t(`sharedContacts.kinds.${match.kind}`)}
            {!match.active && ` · ${t('sharedContacts.inactive')}`}
            {' — '}
            {t('sharedContacts.shares', {
              fields: formatList(match.shared.map(fieldName), i18n.language),
            })}
          </li>
        ))}
      </ul>
    </section>
  )
}
